// src/SignUpMFA.jsx
import { useEffect, useRef, useState } from "react";
import { auth, db } from "./firebase";
import {
  RecaptchaVerifier,
  PhoneAuthProvider,
  multiFactor,
  PhoneMultiFactorGenerator,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const TRELLI_GREEN = "#00B074";

export default function SignUpMFA() {
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("enterPhone"); // enterPhone | enterCode | done
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const verificationIdRef = useRef(null);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    // If no user, bounce to login
    if (!auth.currentUser) {
      nav("/");
      return;
    }
    // Prepare a visible reCAPTCHA container we can reuse
    if (!recaptchaRef.current) {
      const el = document.createElement("div");
      el.id = "mfa-recaptcha";
      el.style.marginTop = "8px";
      document.body.appendChild(el);
      recaptchaRef.current = el;
    }

    // Create a verifier if we don't already have one
    if (!window._trelliMfaRecaptcha) {
      try {
        window._trelliMfaRecaptcha = new RecaptchaVerifier(
          auth,
          "mfa-recaptcha",
          {
            size: "invisible", // change to "normal" if you want a visible widget
          }
        );
      } catch {
        // If we already created one in this session, ignore
      }
    }
  }, [nav]);

  async function sendCode(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Not signed in.");

      // 1) Get an MFA session from the user
      const mfaSession = await multiFactor(user).getSession();

      // 2) Request SMS with reCAPTCHA
      const phoneInfoOptions = { phoneNumber: phone.trim(), session: mfaSession };
      const provider = new PhoneAuthProvider(auth);
      const verificationId = await provider.verifyPhoneNumber(
        phoneInfoOptions,
        window._trelliMfaRecaptcha
      );
      verificationIdRef.current = verificationId;
      setStep("enterCode");
    } catch (e) {
      setErr(cleanError(e));
    } finally {
      setBusy(false);
    }
  }

  async function verifyAndEnroll(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Not signed in.");

      const verificationId = verificationIdRef.current;
      if (!verificationId) throw new Error("No verification in progress.");

      // 3) Create phone credential and enroll as MFA factor
      const cred = PhoneAuthProvider.credential(verificationId, code.trim());
      const assertion = PhoneMultiFactorGenerator.assertion(cred);

      await multiFactor(user).enroll(assertion, "My Phone");

      // 4) Mark in Firestore
      const userRef = doc(db, "users", user.uid);
      await setDoc(
        userRef,
        {
          mfaEnrolled: true,
          mfaEnrolledAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setStep("done");
    } catch (e) {
      setErr(cleanError(e));
    } finally {
      setBusy(false);
    }
  }

  if (!auth.currentUser) return null;

  return (
    <div style={wrap}>
      <div style={card}>
        <h2 style={{ marginTop: 0 }}>Secure your account (2FA)</h2>
        <p style={{ color: "#475467", marginTop: 4 }}>
          Add your phone as a second factor. This helps keep your data safe.
        </p>

        {err && <div style={errorBox}>{err}</div>}

        {step === "enterPhone" && (
          <form onSubmit={sendCode} style={{ display: "grid", gap: 10 }}>
            <label style={label}>Phone number</label>
            <input
              type="tel"
              required
              placeholder="+1 555 555 5555"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={input}
              autoComplete="tel"
            />
            <button type="submit" disabled={busy} style={primary}>
              {busy ? "Sending code…" : "Send code"}
            </button>
            <button type="button" onClick={() => nav("/")} style={linkUnder}>
              Do this later
            </button>
          </form>
        )}

        {step === "enterCode" && (
          <form onSubmit={verifyAndEnroll} style={{ display: "grid", gap: 10 }}>
            <label style={label}>Enter verification code</label>
            <input
              type="text"
              inputMode="numeric"
              required
              placeholder="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              style={input}
            />
            <button type="submit" disabled={busy} style={primary}>
              {busy ? "Verifying…" : "Verify & enable 2FA"}
            </button>
            <button
              type="button"
              onClick={() => setStep("enterPhone")}
              style={linkBtn}
            >
              Back
            </button>
          </form>
        )}

        {step === "done" && (
          <div>
            <div style={successBox}>Two-factor authentication is enabled ✅</div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button onClick={() => nav("/wizard")} style={primary}>
                Continue setup
              </button>
              <button onClick={() => nav("/dashboard")} style={linkBtn}>
                Go to dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* utils + styles */
function cleanError(e) {
  const msg = e?.message || String(e || "");
  // Make common Firebase phone auth errors friendlier
  if (msg.includes("missing-phone-number")) return "Please enter a phone number.";
  if (msg.includes("invalid-phone-number")) return "That phone number looks invalid.";
  if (msg.includes("too-many-requests")) return "Too many attempts — please wait and try again.";
  if (msg.includes("captcha-check-failed")) return "reCAPTCHA failed — retry.";
  if (msg.includes("invalid-verification-code")) return "That code is incorrect — try again.";
  return msg;
}

const wrap = {
  minHeight: "100dvh",
  display: "grid",
  placeItems: "center",
  background:
    "radial-gradient(1200px 600px at 10% -20%, rgba(0,176,116,0.08), transparent 50%), radial-gradient(800px 400px at 110% 120%, rgba(244,197,66,0.06), transparent 40%), #f8faf9",
  padding: 24,
};
const card = {
  width: "100%",
  maxWidth: 520,
  background: "#fff",
  borderRadius: 16,
  boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
  padding: 24,
};
const label = { fontSize: 13, color: "#444", marginBottom: 6, fontWeight: 600 };
const input = {
  height: 42,
  borderRadius: 10,
  border: "1px solid #dce1e5",
  padding: "0 12px",
  outline: "none",
  fontSize: 15,
  background: "#fff",
};
const primary = {
  marginTop: 12,
  height: 44,
  border: "none",
  borderRadius: 12,
  background: TRELLI_GREEN,
  color: "#fff",
  fontWeight: 800,
  fontSize: 15,
  cursor: "pointer",
  boxShadow: "0 4px 10px rgba(0,176,116,0.25)",
  padding: "0 16px",
};
const linkBtn = {
  height: 44,
  borderRadius: 12,
  border: "1px solid #E5E7EB",
  background: "#fff",
  color: "#111",
  fontWeight: 700,
  cursor: "pointer",
  padding: "0 16px",
};
const linkUnder = {
  marginTop: 10,
  border: "none",
  background: "transparent",
  color: TRELLI_GREEN,
  fontWeight: 700,
  textDecoration: "underline",
  cursor: "pointer",
};
const errorBox = {
  border: "1px solid #ef9a9a",
  background: "#ffebee",
  color: "#b71c1c",
  borderRadius: 10,
  padding: "10px 12px",
  marginBottom: 12,
  fontSize: 14,
};
const successBox = {
  border: "1px solid #c8e6c9",
  background: "#e8f5e9",
  color: "#1b5e20",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 14,
};
